import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Printer, Images } from "lucide-react";
import { Volume2, Layers, Search, Inbox, RotateCcw } from "lucide-react";
import { api, speak, THEMES } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { VocabFlashcards } from "@/components/VocabFlashcards";
import { WordIcon } from "@/components/WordIcon";
import { useTranslations, Translation } from "@/lib/translations";
import { Mic, Camera } from "lucide-react";

export default function VocabularyPage() {
  const [words, setWords] = useState(null);
  const [q, setQ] = useState("");
  const [theme, setTheme] = useState("Tous");
  const [review, setReview] = useState(null);
  const [params, setParams] = useSearchParams();
  const [teamReviewId, setTeamReviewId] = useState(null);
  const [{ wantReview, wantTeam }] = useState(() => ({ wantReview: params.has("revoir"), wantTeam: params.has("equipe") }));

  useEffect(() => {
    if (params.has("revoir") || params.has("equipe")) setParams({}, { replace: true });
  }, [params, setParams]);

  useEffect(() => {
    let alive = true;
    if (wantTeam)
      api.get("/teams/reviews/latest").then((r) => {
        if (alive && r.data?.words?.length) { setTeamReviewId(r.data.id); setReview(r.data.words); }
      });
    api.get("/vocabulary").then((r) => {
      if (!alive) return;
      setWords(r.data);
      const missed = r.data.filter((w) => w.to_review);
      if (wantReview && missed.length) setReview(missed);
    });
    return () => { alive = false; };
  }, [wantReview, wantTeam]);

  const shown = useMemo(
    () => (words || []).filter((w) => (theme === "Tous" || w.theme === theme) && `${w.word} ${w.definition}`.toLowerCase().includes(q.toLowerCase())),
    [words, q, theme]
  );
  const toReview = shown.filter((w) => w.to_review);
  const tr = useTranslations(shown.slice(0, 30));
  const onMark = (word, flag) => setWords((ws) => ws.map((w) => (w.word === word ? { ...w, to_review: flag } : w)));

  if (!words) return <Skeleton className="h-40 w-full" />;
  if (review)
    return (
      <VocabFlashcards
        words={review}
        onMark={onMark}
        onFinish={() => teamReviewId && localStorage.setItem("btp_team_review_done", teamReviewId)}
        onClose={() => { setReview(null); setTeamReviewId(null); }}
      />
    );

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-3">
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold uppercase">Mes mots</h1>
        <span className="font-display text-2xl font-bold text-orange-600" data-testid="vocab-count">{words.length}</span>
      </div>

      {words.length === 0 ? (
        <div className="bg-white rounded-2xl border-2 border-dashed border-slate-300 p-8 text-center text-slate-600" data-testid="vocab-empty">
          <Inbox className="w-10 h-10 mx-auto mb-2 text-slate-400" />
          Terminez une leçon pour retrouver ses mots ici.
        </div>
      ) : (
        <>
          {toReview.length > 0 && (
            <button
              onClick={() => setReview(toReview)}
              data-testid="vocab-review-missed-btn"
              className="w-full h-14 rounded-xl bg-orange-500 hover:bg-orange-600 active:scale-[.98] text-white font-display text-xl font-bold uppercase flex items-center justify-center gap-2 transition-[background-color,transform]"
            >
              <RotateCcw className="w-6 h-6" /> Mots à revoir ({toReview.length})
            </button>
          )}
          <button
            onClick={() => setReview(shown)}
            disabled={!shown.length}
            data-testid="vocab-review-btn"
            className={`w-full h-14 rounded-xl active:scale-[.98] font-display text-xl font-bold uppercase flex items-center justify-center gap-2 transition-[background-color,transform] disabled:opacity-40 ${
              toReview.length ? "border-2 border-slate-900 hover:bg-slate-900 hover:text-white" : "bg-orange-500 hover:bg-orange-600 text-white"
            }`}
          >
            <Layers className="w-6 h-6" /> Tout réviser ({shown.length})
          </button>
          <Link
            to="/photo-outil"
            data-testid="vocab-photo-identify-link"
            className="w-full h-12 rounded-xl bg-white border-2 border-slate-900 hover:bg-slate-900 hover:text-white font-bold flex items-center justify-center gap-2 transition-colors"
          >
            <Camera className="w-5 h-5" /> Photographier un outil
          </Link>
          <Link
            to="/prononciation"
            data-testid="vocab-pronunciation-link"
            className="w-full h-12 rounded-xl bg-white border-2 border-orange-500 text-orange-600 hover:bg-orange-50 font-bold flex items-center justify-center gap-2 transition-colors"
          >
            <Mic className="w-5 h-5" /> Prononciation à voix haute
          </Link>
          <Link
            to="/quiz-images"
            data-testid="vocab-image-quiz-link"
            className="w-full h-12 rounded-xl bg-slate-900 text-amber-400 hover:bg-slate-800 font-bold flex items-center justify-center gap-2 transition-colors"
          >
            <Images className="w-5 h-5" /> Quiz en images
          </Link>
          <Link
            to="/imprimer"
            data-testid="vocab-print-link"
            className="w-full h-12 rounded-xl border-2 border-dashed border-slate-400 hover:border-orange-500 hover:text-orange-600 font-bold flex items-center justify-center gap-2 transition-colors"
          >
            <Printer className="w-5 h-5" /> Imprimer ma fiche de poche
          </Link>
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Chercher un mot" className="h-12 pl-10 text-base bg-white" data-testid="vocab-search-input" />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4" data-testid="vocab-theme-filter">
            {["Tous", ...THEMES].map((t) => (
              <button
                key={t}
                onClick={() => setTheme(t)}
                data-testid={`vocab-theme-${t}`}
                className={`shrink-0 h-10 px-4 rounded-full border-2 text-sm font-bold transition-colors ${theme === t ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 hover:border-slate-400"}`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="space-y-2" data-testid="vocab-list">
            {shown.map((w, i) => (
              <div key={w.word} className="bg-white rounded-xl border-2 border-slate-200 p-4 flex items-start gap-3" data-testid={`vocab-item-${i}`}>
                <WordIcon word={w} size="sm" testId={`vocab-item-icon-${i}`} />
                <div className="flex-1">
                  <div className="font-bold text-lg">{w.word}</div>
                  <Translation text={tr.map[w.word]} rtl={tr.rtl} testId={`vocab-item-translation-${i}`} />
                  <div className="text-sm text-slate-600">{w.definition}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs font-bold uppercase text-orange-600">{w.theme}</span>
                    {w.to_review && (
                      <span className="text-[10px] font-bold uppercase bg-red-100 text-red-700 rounded-full px-2 py-0.5" data-testid={`vocab-item-review-tag-${i}`}>À revoir</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => speak(`${w.word}. ${w.example || ""}`, 0.9)}
                  aria-label="Écouter"
                  data-testid={`vocab-item-audio-${i}`}
                  className="shrink-0 w-11 h-11 rounded-full bg-slate-900 text-amber-400 hover:bg-orange-500 hover:text-white grid place-items-center transition-colors"
                >
                  <Volume2 className="w-5 h-5" />
                </button>
              </div>
            ))}
            {!shown.length && <p className="text-slate-500 text-center py-6" data-testid="vocab-no-result">Aucun mot trouvé.</p>}
          </div>
        </>
      )}
    </div>
  );
}
